import React, { useState } from 'react';
import axiosInstance from '../utils/axiosInstance';

// Approve modal: staff assigns the two identifiers the system genuinely
// requires (studentId, registrationNumber) and confirms -- reuses the exact
// same create-student / create-registration endpoints the rest of the admin
// app uses, so this submission becomes a completely normal registration.
// Shared between the live QR page and the Register Pool batch page's
// pending-registrations list.
export default function RegisterPoolApproveModal({ submission, batchId, onClose, onApproved }) {
  // Pre-filled from what the student themselves entered on the form (if
  // any) -- staff can still correct either before confirming.
  const [studentId, setStudentId] = useState(submission.studentId || '');
  const [registrationNumber, setRegistrationNumber] = useState(submission.registrationNumber || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    const trimmedId = studentId.trim();
    const trimmedReg = registrationNumber.trim();
    if (!trimmedId || !trimmedReg) {
      setError('Both Student ID and Registration Number are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      try {
        await axiosInstance.post('/admin/students', {
          studentId: trimmedId,
          fullName: submission.fullName,
          email: submission.email,
          telephone: submission.telephone,
          dob: submission.dob,
          gender: submission.gender,
          nicNumber: submission.nicNumber,
          address: submission.address,
        });
      } catch (err) {
        // Student ID already belongs to an existing student -- register them
        // into this batch instead of failing the whole approval.
        if (err?.response?.data?.message !== 'Student ID already exists') throw err;
      }

      const regResp = await axiosInstance.post('/admin/registrations', {
        studentId: trimmedId,
        batchId,
        registrationNumber: trimmedReg,
        registrationStatus: 'active',
      });
      const registrationId = regResp.data?.registration?.registrationId;

      await axiosInstance.post(`/admin/register-pool/submissions/${submission.id}/approve`, {
        studentId: trimmedId,
        registrationId,
      });

      onApproved();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to approve this registration.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold text-slate-800">Approve Registration</h3>
        <p className="mt-1 text-sm text-slate-500">{submission.fullName}</p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Student ID *</label>
            <input
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              autoFocus
              placeholder="e.g. E123456"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Registration Number *</label>
            <input
              value={registrationNumber}
              onChange={(e) => setRegistrationNumber(e.target.value)}
              placeholder="e.g. 012547"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
          </div>
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Approving…' : 'Approve & Register'}
          </button>
        </div>
      </div>
    </div>
  );
}
